import {
  Ticket,
  MessageSquare,
  Star,
  UserCircle,
  Pencil,
  Mail,
  Clock,
  AlertTriangle,
  GitMerge,
  Tag,
  ArrowRight,
  ArrowUpCircle,
  Shield,
  UserPlus,
  UserMinus,
  MessageCircle,
  Plus,
  RotateCcw,
  XCircle,
  CheckCircle,
  Paperclip,
  StickyNote,
  FileText,
  ListChecks,
  Bot,
  KeyRound,
  type LucideIcon,
} from 'lucide-react';
import { isSystemField } from '@utils/systemFields';

export interface HistoryIconConfig {
  icon: LucideIcon;
  color: string;
  bgColor: string;
}

export function getHistoryIcon(eventType: string): HistoryIconConfig {
  const map: Record<string, HistoryIconConfig> = {
    // Ticket events
    'ticket.created': { icon: Plus, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    // Grouped multi-field edit (2+ fields changed in one save).
    'ticket.updated': { icon: Pencil, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket.status_changed': { icon: ArrowRight, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket.priority_changed': { icon: AlertTriangle, color: 'text-orange-600', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
    'ticket.assigned': { icon: UserPlus, color: 'text-indigo-600', bgColor: 'bg-indigo-100 dark:bg-indigo-900/30' },
    'ticket.unassigned': { icon: UserMinus, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.secondary_assignee_added': { icon: UserPlus, color: 'text-indigo-600', bgColor: 'bg-indigo-100 dark:bg-indigo-900/30' },
    'ticket.secondary_assignee_removed': { icon: UserMinus, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    // Access requests from @mentioned agents who could not see the ticket.
    'ticket.access_requested': { icon: KeyRound, color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
    'ticket.access_request_approved': { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'ticket.access_request_rejected': { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
    'ticket.access_request_cancelled': { icon: XCircle, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.stage_changed': { icon: ArrowRight, color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
    'ticket.pipeline_changed': { icon: GitMerge, color: 'text-cyan-600', bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' },
    'ticket.message_sent': { icon: MessageSquare, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket.message_received': { icon: MessageCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'ticket.internal_note': { icon: Pencil, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
    'ticket.sla_breach': { icon: Shield, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
    'ticket.sla_warning': { icon: AlertTriangle, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
    'ticket.merged': { icon: GitMerge, color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
    'ticket.tag_added': { icon: Tag, color: 'text-teal-600', bgColor: 'bg-teal-100 dark:bg-teal-900/30' },
    'ticket.tag_removed': { icon: Tag, color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.tags_changed': { icon: Tag, color: 'text-teal-600', bgColor: 'bg-teal-100 dark:bg-teal-900/30' },
    'ticket.resolved': { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'ticket.reopened': { icon: RotateCcw, color: 'text-orange-600', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
    'ticket.closed': { icon: XCircle, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.category_changed': { icon: ArrowRight, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket.type_changed': { icon: ArrowRight, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket.custom_field_changed': { icon: Pencil, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    // Collapsed stand-in for automation-only field writes (see normalizeHistoryEntry).
    'ticket.automation_data_updated': { icon: Bot, color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.custom_fields_changed': { icon: Pencil, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.field_changed': { icon: Pencil, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.first_response': { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'ticket.follower_added': { icon: UserPlus, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.follower_removed': { icon: UserMinus, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },

    // Notes & line items
    'ticket.note_added': { icon: StickyNote, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
    'ticket.note_updated': { icon: StickyNote, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
    'ticket.note_deleted': { icon: StickyNote, color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.note_pinned': { icon: StickyNote, color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
    'ticket.note_unpinned': { icon: StickyNote, color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.line_items_added': { icon: ListChecks, color: 'text-teal-600', bgColor: 'bg-teal-100 dark:bg-teal-900/30' },
    'ticket.line_item_updated': { icon: ListChecks, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    // Line items brought up to date from a customer's email reply (added / updated / to review).
    'ticket.line_items_synced_from_email': { icon: ListChecks, color: 'text-teal-600', bgColor: 'bg-teal-100 dark:bg-teal-900/30' },
    // One granular field change nested inside a grouped `line_item_updated` row.
    'ticket.line_item_field_changed': { icon: Pencil, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket.line_item_status_changed': { icon: ListChecks, color: 'text-indigo-600', bgColor: 'bg-indigo-100 dark:bg-indigo-900/30' },
    'ticket.line_item_deleted': { icon: ListChecks, color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.quote_generated': { icon: FileText, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'ticket.quote_failed': { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },

    // Attachments & SLA
    'ticket.attachment_added': { icon: Paperclip, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket.attachment_removed': { icon: Paperclip, color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-700' },
    'ticket.sla_breached': { icon: Shield, color: 'text-red-600', bgColor: 'bg-red-100 dark:bg-red-900/30' },
    // Escalation climbs a ladder, so it gets its own arrow rather than the
    // breach shield — an escalated ticket is not necessarily breached (a level
    // can fire ahead of the deadline).
    'ticket.sla_escalated': { icon: ArrowUpCircle, color: 'text-orange-600', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
    'ticket.sla_met': { icon: Shield, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'ticket.survey_sent': { icon: Star, color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },

    // Customer events
    'customer.created': { icon: Plus, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'customer.updated': { icon: Pencil, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'customer.ticket_created': { icon: Ticket, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'customer.ticket_resolved': { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'customer.message_sent': { icon: MessageSquare, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'customer.message_received': { icon: MessageCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'customer.note_added': { icon: Pencil, color: 'text-yellow-600', bgColor: 'bg-yellow-100 dark:bg-yellow-900/30' },
    'customer.profile_updated': { icon: UserCircle, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'customer.tag_changed': { icon: Tag, color: 'text-teal-600', bgColor: 'bg-teal-100 dark:bg-teal-900/30' },
    'customer.merged': { icon: GitMerge, color: 'text-purple-600', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
    'customer.login': { icon: UserCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'customer.chat_started': { icon: MessageCircle, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'customer.chat_ended': { icon: MessageCircle, color: 'text-gray-600', bgColor: 'bg-gray-100 dark:bg-gray-700' },

    // Legacy types for backward compat
    'ticket_created': { icon: Ticket, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket_updated': { icon: Pencil, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'ticket_resolved': { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'message_sent': { icon: MessageSquare, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'message_received': { icon: MessageCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'rating_submitted': { icon: Star, color: 'text-amber-600', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
    'login': { icon: UserCircle, color: 'text-green-600', bgColor: 'bg-green-100 dark:bg-green-900/30' },
    'profile_updated': { icon: UserCircle, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
    'email_opened': { icon: Mail, color: 'text-blue-600', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  };

  return map[eventType] || { icon: Clock, color: 'text-gray-500', bgColor: 'bg-gray-100 dark:bg-gray-700' };
}

/**
 * One line inside a grouped "ticket.updated" entry, split into a `label` and an
 * optional `detail`, so the UI can render it as `Priority changed: Low → High`
 * or `Tag added: "urgent"`. Derived from each change's own metadata (old/new
 * values), which the backend nests inside the group's metadata.changes array.
 */
export interface GroupChangeLine {
  label: string;
  detail?: string;
}

// "in_progress" → "In Progress", "low" → "Low". Used for enum-y codes only.
const humanizeCode = (v: unknown): string =>
  String(v ?? '')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const quote = (v: unknown): string => `"${String(v ?? '')}"`;

const truncate = (v: unknown, max = 40): string => {
  const s = String(v ?? '');
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

// `"From" → "To"`, gracefully collapsing set (no from) / cleared (no to).
const arrow = (from: unknown, to: unknown, transform: (v: unknown) => string = quote): string => {
  const hasFrom = from != null && String(from).length > 0;
  const hasTo = to != null && String(to).length > 0;
  if (hasFrom && hasTo) return `${transform(from)} → ${transform(to)}`;
  if (hasTo) return transform(to);
  if (hasFrom) return '(removed)';
  return '';
};

// Nice labels for the tracked plain fields (metadata.field is the raw key).
const FIELD_LABELS: Record<string, string> = {
  subject: 'Subject',
  description: 'Description',
  contactEmail: 'Contact email',
  contactName: 'Contact name',
};

export function describeGroupChange(change: {
  eventType?: string;
  title?: string;
  metadata?: Record<string, unknown>;
}): GroupChangeLine {
  const m = (change.metadata || {}) as Record<string, any>;
  switch (change.eventType) {
    case 'ticket.tag_added':
      return { label: 'Tag added', detail: quote(m.tag) };
    case 'ticket.tag_removed':
      return { label: 'Tag removed', detail: quote(m.tag) };
    case 'ticket.priority_changed':
      return { label: 'Priority changed', detail: arrow(m.oldPriority, m.newPriority, humanizeCode) };
    case 'ticket.status_changed':
      return { label: 'Status changed', detail: arrow(m.oldStatus, m.newStatus, humanizeCode) };
    case 'ticket.type_changed':
      return { label: 'Type changed', detail: arrow(m.oldTicketTypeName, m.newTicketTypeName) };
    case 'ticket.pipeline_changed':
      return { label: 'Pipeline changed', detail: arrow(m.oldPipelineName, m.newPipelineName) };
    case 'ticket.stage_changed':
      return { label: 'Stage changed', detail: arrow(m.fromStageName, m.toStageName) };
    case 'ticket.custom_field_changed':
    // A line item's own attributes (name, status, due date, assignee) and its
    // detail-grid fields all arrive in this shape, already label-resolved.
    case 'ticket.line_item_field_changed':
      return {
        label: `${m.fieldLabel || m.field || 'Field'} changed`,
        detail: arrow(m.from, m.to),
      };
    case 'ticket.field_changed': {
      const label = `${FIELD_LABELS[m.field] || m.field || 'Field'} changed`;
      // Description diffs are long/noisy — show a short preview, not full text.
      if (m.field === 'description') return { label, detail: arrow(truncate(m.from), truncate(m.to)) };
      return { label, detail: arrow(m.from, m.to) };
    }
    default:
      // Unknown change type — fall back to the pre-built sentence title.
      return { label: change.title || change.eventType || 'Changed' };
  }
}

export const AUTOMATION_DATA_EVENT = 'ticket.automation_data_updated';
export const AUTOMATION_DATA_TITLE = 'Automation updated internal data';

/** Shape of one nested change inside a grouped `ticket.updated` entry. */
export interface RawGroupChange {
  eventType?: string;
  title?: string;
  field?: string;
  metadata?: Record<string, unknown>;
}

/**
 * What the timeline should actually render for an entry, after internal
 * automation fields are stripped.
 */
export interface NormalizedHistoryEntry {
  eventType: string;
  title: string;
  /** Group children to offer under "Show N changes"; empty = render as a single row. */
  changes: RawGroupChange[];
}

/**
 * Event types whose entries are expandable groups: the row is a summary and
 * `metadata.changes` holds one granular change per field touched in that save.
 *
 *  - `ticket.updated` — 2+ ticket fields changed in one save
 *  - `ticket.line_item_updated` — 2+ fields changed on one line item
 *  - `ticket.line_items_synced_from_email` — what a customer reply added,
 *    updated, or left for an agent to review
 */
const GROUPED_EVENTS = new Set(['ticket.updated', 'ticket.line_item_updated', 'ticket.line_items_synced_from_email']);

/** Change event types that carry a single field key we can screen for internals. */
const FIELD_CHANGE_EVENTS = new Set([
  'ticket.custom_field_changed',
  // Automation writes the same failure/quote cluster onto a line item's details
  // (workflow-service maps webhook responses there), so line-item field changes
  // need the same screening — otherwise the timeline shows
  // `Line item "quote": failureReason, failedItemsHtml, … updated`.
  'ticket.line_item_field_changed',
]);

/** True for a change describing a write to an automation-internal field. */
function isInternalFieldChange(change: RawGroupChange): boolean {
  if (!FIELD_CHANGE_EVENTS.has(change.eventType || '')) return false;
  const key = String(change.metadata?.field ?? change.field ?? '');
  return key.length > 0 && isSystemField(key);
}

/**
 * Legacy line-item summary shape: before the backend recorded granular changes,
 * a multi-field edit was stored as a bare comma list with no values and no
 * `metadata.changes` — e.g.
 *   `Line item "quote": failureReason, failedItemsHtml, quoteFailedMpan updated`
 * Automation writes 8 of those keys in one PUT, so the stored rows are pure
 * noise to an agent. There is nothing to expand and no values were ever kept,
 * but the field names are right there in the title, so screen them out at
 * display time. Anchored on the trailing " updated" so it can't match the
 * value-carrying shapes (`…: Quantity changed from "2" to "5"`,
 * `… updated · 3 changes`).
 */
const LEGACY_LINE_ITEM_SUMMARY = /^(Line item "[^"]*"): (.+) updated$/;

function normalizeLegacyLineItemSummary(title: string): string | null {
  const match = LEGACY_LINE_ITEM_SUMMARY.exec(title);
  if (!match) return null;

  const [, prefix, list] = match;
  const fields = list.split(',').map((f) => f.trim()).filter(Boolean);
  // Only treat it as a field list if every token looks like a bare field key —
  // otherwise it's prose we shouldn't rewrite.
  if (fields.length === 0 || fields.some((f) => /\s/.test(f))) return null;

  const visible = fields.filter((f) => !isSystemField(f));
  if (visible.length === fields.length) return null; // nothing internal — leave it alone
  if (visible.length === 0) return `${prefix}: ${AUTOMATION_DATA_TITLE}`;
  return `${prefix}: ${visible.join(', ')} updated`;
}

/**
 * Rewrite (or append) the trailing "· N changes" count on a group summary
 * title, so a recount after dropping internal changes never advertises rows the
 * agent can no longer expand. Handles both `Ticket updated · 3 changes` and
 * `Line item "Widget A" updated · 3 changes`.
 */
function withChangeCount(title: string, n: number): string {
  const suffix = `· ${n} change${n === 1 ? '' : 's'}`;
  const existing = /·\s*\d+\s+changes?$/;
  return existing.test(title) ? title.replace(existing, suffix) : `${title} ${suffix}`;
}

/**
 * Collapse automation-internal field writes into one neutral line.
 *
 * The backend now does this at write time, but rows recorded before that still
 * carry them — a single automation PUT (e.g. a failed quote validation) wrote 8
 * `custom_field_changed` entries whose values are raw HTML tables, which the
 * grouped view then dumped onto the agent's timeline. This normalizes both
 * shapes:
 *
 *  - a grouped `ticket.updated` or `ticket.line_item_updated` row → internal
 *    changes dropped and the "· N changes" count recomputed; if nothing
 *    agent-facing is left, the whole row becomes the neutral automation line
 *    (line items keep their `Line item "…"` prefix);
 *  - a standalone internal `custom_field_changed` row → the neutral line;
 *  - a legacy `ticket.line_item_updated` row with no granular changes, whose
 *    title is a bare comma list of internal keys → the keys are screened out of
 *    the title (see normalizeLegacyLineItemSummary).
 *
 * Everything else (including customer-activity entries) passes through
 * untouched.
 */
export function normalizeHistoryEntry(entry: {
  eventType?: string;
  type?: string;
  title?: string;
  metadata?: Record<string, unknown> | null;
}): NormalizedHistoryEntry {
  const eventType = entry.eventType || entry.type || '';
  const title = entry.title || '';
  const meta = (entry.metadata || {}) as Record<string, unknown>;

  const automationLine: NormalizedHistoryEntry = {
    eventType: AUTOMATION_DATA_EVENT,
    title: AUTOMATION_DATA_TITLE,
    changes: [],
  };

  // Standalone internal field change (single-field automation write).
  if (eventType === 'ticket.custom_field_changed') {
    return isInternalFieldChange({ eventType, metadata: meta })
      ? automationLine
      : { eventType, title, changes: [] };
  }

  if (!GROUPED_EVENTS.has(eventType) || !Array.isArray(meta.changes)) {
    // No granular changes to work with. Line-item rows written before the
    // backend recorded them still name the internal fields in the title, so
    // clean those up rather than showing an agent eight automation keys.
    if (eventType === 'ticket.line_item_updated') {
      const cleaned = normalizeLegacyLineItemSummary(title);
      if (cleaned) return { eventType, title: cleaned, changes: [] };
    }
    return { eventType, title, changes: [] };
  }

  const all = meta.changes as RawGroupChange[];
  const visible = all.filter((c) => !isInternalFieldChange(c));

  // Nothing but internal plumbing — one neutral line, no expansion. A line-item
  // group keeps its `Line item "quote"` prefix so the agent still knows which
  // item automation touched.
  if (visible.length === 0) {
    if (eventType === 'ticket.line_item_updated') {
      const prefix = /^(Line item "[^"]*")/.exec(title)?.[1];
      if (prefix) {
        return { eventType, title: `${prefix}: ${AUTOMATION_DATA_TITLE}`, changes: [] };
      }
    }
    return automationLine;
  }

  // Some internal changes were dropped: rebuild the count so the summary
  // doesn't advertise changes the agent can no longer expand. Automation still
  // gets its own line inside the group so the trace isn't lost.
  if (visible.length !== all.length) {
    const changes = [
      ...visible,
      { eventType: AUTOMATION_DATA_EVENT, title: AUTOMATION_DATA_TITLE },
    ];
    return { eventType, title: withChangeCount(title, changes.length), changes };
  }

  return { eventType, title, changes: all };
}

/** One rendered detail row of an SLA-escalation timeline entry. */
export interface SlaEscalationDetail {
  label: string;
  value: string;
}

export interface SlaEscalationView {
  /** 'approaching_breach' drives the amber treatment, 'breached' the red one. */
  reason: 'approaching_breach' | 'breached';
  reasonLabel: string;
  details: SlaEscalationDetail[];
}

const SLA_ESCALATED_EVENT = 'ticket.sla_escalated';

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

/**
 * The structured detail block for a `ticket.sla_escalated` entry.
 *
 * Every string here is rendered by ticket-service at WRITE time from the shared
 * escalation vocabulary (`escalationReasonLabel`, `formatEscalationDuration`,
 * …), the same helpers the escalation email uses. Nothing is re-derived or
 * re-worded here on purpose: a second formatter on the read side is exactly how
 * the timeline and the email would drift apart.
 *
 * Fields are rendered only when present, so an entry written before Task 6 (no
 * previous/new target, no pre-rendered labels) degrades to the rows it does
 * have instead of printing "undefined".
 *
 * Returns null for every other event type — this must never change how any
 * other timeline row renders.
 */
export function getSlaEscalationView(
  eventType: string,
  metadata: Record<string, unknown> | null | undefined,
): SlaEscalationView | null {
  if (eventType !== SLA_ESCALATED_EVENT) return null;
  const meta = (metadata || {}) as Record<string, unknown>;

  const reason = meta.triggerReason === 'approaching_breach' ? 'approaching_breach' : 'breached';
  const reasonLabel =
    text(meta.reasonLabel) ?? (reason === 'approaching_breach' ? 'Approaching breach' : 'SLA breached');

  const details: SlaEscalationDetail[] = [];
  const push = (label: string, value: string | undefined) => {
    if (value) details.push({ label, value });
  };

  push('Previous target', text(meta.previousTargetLabel));
  push('New target', text(meta.newTargetLabel));
  push('Elapsed time', text(meta.elapsedBusinessLabel));
  push('SLA policy', text(meta.slaPolicyName));

  const target = text(meta.targetLabel);
  if (target) push('SLA target', `${target} SLA`);

  const status = text(meta.notificationStatus);
  if (status) {
    push(
      'Notification',
      {
        delivered: 'Delivered',
        partial: 'Partially delivered',
        failed: 'Delivery failed',
        // Configured, but every recipient's own preferences (or quiet hours)
        // suppressed delivery — distinct from nothing being configured (#18925).
        suppressed: 'Suppressed by recipient preferences',
        none: 'No notification configured',
      }[status] ?? status,
    );
  }

  return { reason, reasonLabel, details };
}

const SLA_BREACHED_EVENT = 'ticket.sla_breached';

/**
 * Append the SLA breach time to a `ticket.sla_breached` title, formatted in the
 * viewer's local timezone.
 *
 * ticket-service records the breach instant as an ISO timestamp in
 * `metadata.breachAt` and deliberately leaves the time out of the stored title:
 * the context-less SLA monitor has no viewer timezone, so the old code baked a
 * hard-coded UTC string, which made the history read UTC while the SLA panel
 * (and every other timestamp) read the viewer's zone (BUG-019 / #18927).
 * Formatting here, from the raw timestamp, keeps the two consistent.
 *
 * Rows written before this change have no `breachAt` and already carry the time
 * in their title, so they are returned unchanged.
 */
export function getSlaBreachTitle(
  eventType: string,
  title: string,
  metadata: Record<string, unknown> | null | undefined,
): string {
  if (eventType !== SLA_BREACHED_EVENT) return title;
  const breachAt = text((metadata || {}).breachAt);
  if (!breachAt) return title;
  const date = new Date(breachAt);
  if (Number.isNaN(date.getTime())) return title;
  const when = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${title} (at ${when})`;
}

export function formatHistoryDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    hour: '2-digit',
    minute: '2-digit',
  });
}
